import DOMPurify from 'dompurify';
import { findCounterArrays, counterTextToSpeech } from './counterModels.js';
import {
  containsMathContent,
  escapeMathHtml,
  renderMathContentToHtml
} from './mathContent.js';
import {
  createSpeechDisplayMap,
  mapSpeechRangeToDisplayRange
} from './ttsRuntime.js';

let browserPurifier = null;
let purifierWindow = null;

function getPurifier() {
  if (typeof DOMPurify?.sanitize === 'function') return DOMPurify;
  if (typeof window === 'undefined' || typeof DOMPurify !== 'function') return null;
  if (!browserPurifier || purifierWindow !== window) {
    browserPurifier = DOMPurify(window);
    purifierWindow = window;
  }
  return browserPurifier;
}

const PURIFY_OPTIONS = Object.freeze({
  ALLOWED_TAGS: [
    'p', 'br', 'div', 'span', 'strong', 'em', 'b', 'i', 'u', 's', 'sub', 'sup',
    'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'pre', 'code'
  ],
  ALLOWED_ATTR: ['colspan', 'rowspan', 'scope', 'aria-label', 'role'],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'link', 'meta', 'base', 'img', 'video', 'audio', 'svg', 'math'],
  FORBID_ATTR: ['style', 'class', 'id', 'src', 'srcset', 'href', 'formaction', 'xlink:href']
});

export function containsRichMarkup(value) {
  return /<[a-z][\s\S]*>/i.test(String(value ?? ''));
}

export function sanitizeRichContentWithMath(value) {
  const raw = String(value ?? '');
  const purifier = getPurifier();
  if (!purifier) return escapeMathHtml(raw).replace(/\r?\n/g, '<br>');
  const clean = purifier.sanitize(raw, PURIFY_OPTIONS);
  if (typeof document === 'undefined') return clean;
  const template = document.createElement('template');
  template.innerHTML = clean;
  // Preserve BR row boundaries before the ordinary math text-node pass.
  // Only plain leaf blocks qualify; authored tables/emphasis remain intact.
  for (const block of template.content.querySelectorAll('p, pre, div')) {
    if ([...block.childNodes].some(node => node.nodeType !== 3 && node.nodeName !== 'BR')) continue;
    const plain = [...block.childNodes].map(node => node.nodeName === 'BR' ? '\n' : node.textContent).join('');
    if (findCounterArrays(plain).length) block.innerHTML = renderMathContentToHtml(plain);
  }
  for (const table of template.content.querySelectorAll('table')) {
    table.style.cssText = 'border-collapse:collapse;width:100%;font-variant-numeric:tabular-nums;white-space:normal;font-size:inherit;color:inherit';
    for (const cell of table.querySelectorAll('th,td')) {
      cell.style.cssText = 'padding:.55em .7em;border:1px solid #c3d4ce;text-align:center;overflow-wrap:anywhere;vertical-align:middle';
      if (cell.tagName === 'TH') cell.style.background = '#edf5f1';
    }
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'max-width:100%;overflow-x:auto;margin:.7em 0;border-radius:8px;white-space:normal';
    table.replaceWith(wrapper);
    wrapper.append(table);
  }
  // SHOW_TEXT is the DOM constant 4. The literal also works in embedded web
  // views where NodeFilter is not exposed as a global.
  const walker = document.createTreeWalker(template.content, 4);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  for (const node of textNodes) {
    if (node.parentElement?.closest('.assessnow-counter-model')) continue;
    if (!containsMathContent(node.nodeValue)) continue;
    const replacement = document.createElement('span');
    replacement.innerHTML = renderMathContentToHtml(node.nodeValue);
    node.replaceWith(...replacement.childNodes);
  }
  return template.innerHTML;
}

export function richContentPlainText(value) {
  const raw = String(value ?? '');
  const purifier = getPurifier();
  if (!purifier) return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const modelText = purifier.sanitize(raw.replace(/<br\s*\/?\s*>/gi, '\n').replace(/<\/(?:p|div|pre)>/gi, '\n'), { ...PURIFY_OPTIONS, ALLOWED_TAGS: [] });
  if (findCounterArrays(modelText).length) return counterTextToSpeech(modelText).replace(/\s+/g, ' ').trim();
  const clean = purifier.sanitize(raw, { ...PURIFY_OPTIONS, ALLOWED_TAGS: [] });
  if (typeof document === 'undefined') return clean.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
  const template = document.createElement('template');
  template.innerHTML = clean;
  return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
}

export function highlightSanitizedRichContent(safeHtml, readableText, activeSpeechRange) {
  const html = String(safeHtml ?? '');
  if (!html || !activeSpeechRange || typeof document === 'undefined') return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  const walker = document.createTreeWalker(template.content, 4);
  const tokenRecords = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.parentElement?.closest('.assessnow-counter-model')) continue;
    const value = String(node.nodeValue || '');
    for (const match of value.matchAll(/\S+/gu)) {
      tokenRecords.push({
        node,
        text: match[0],
        start: match.index,
        end: match.index + match[0].length
      });
    }
  }
  if (!tokenRecords.length) return html;

  // Joining DOM words with one normalized space produces a stable offset
  // surface while the records retain their exact node-local positions. This
  // preserves paragraphs, lists, tables, emphasis, and other sanitized markup.
  const domSpeechSurface = tokenRecords.map(record => record.text).join(' ');
  const alignment = createSpeechDisplayMap(domSpeechSurface, readableText);
  const domRange = mapSpeechRangeToDisplayRange(activeSpeechRange, alignment);
  if (!domRange) return html;
  const activeRecords = alignment.displayTokens
    .map((token, index) => ({ token, record: tokenRecords[index] }))
    .filter(({ token, record }) => record && token.start < domRange.end && token.end > domRange.start)
    .map(({ record }) => record);
  if (!activeRecords.length) return html;

  const recordsByNode = new Map();
  for (const record of activeRecords) {
    const records = recordsByNode.get(record.node) || [];
    records.push(record);
    recordsByNode.set(record.node, records);
  }
  for (const [node, records] of recordsByNode) {
    const value = String(node.nodeValue || '');
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    records.sort((left, right) => left.start - right.start);
    for (const record of records) {
      if (record.start > cursor) fragment.append(value.slice(cursor, record.start));
      const mark = document.createElement('mark');
      mark.className = 'assessnow-tts-word-highlight';
      mark.setAttribute('data-assessnow-tts-highlight', 'true');
      mark.textContent = value.slice(record.start, record.end);
      fragment.append(mark);
      cursor = record.end;
    }
    if (cursor < value.length) fragment.append(value.slice(cursor));
    node.replaceWith(fragment);
  }
  return template.innerHTML;
}

export function renderAssessmentContentToHtml(value) {
  return containsRichMarkup(value)
    ? sanitizeRichContentWithMath(value)
    : renderMathContentToHtml(value);
}
